import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { DisclosureModal } from "@/components/DisclosureModal";
import { RegionSetupDialog } from "@/components/RegionSetupDialog";
import { OnboardingTour } from "@/components/OnboardingTour";

/** Routes where the first-run modals must NOT fire. /privacy and /terms are
 * the documents the Disclosure modal links to — covering them with the very
 * modal that asks you to read them makes informed consent impossible (the
 * policy rendered behind/under the modal, unreadable). */
const EXEMPT_ROUTES = ["/privacy", "/terms"];

/**
 * Shell-level first-run / compliance gate. Hosted by `GlobalDialogsProvider` so
 * these modals fire on EVERY route — including the new default path (`/` = Home)
 * after the Stage-A cutover. The gating is lifted verbatim from the legacy
 * Canvas so behaviour is unchanged:
 *   1) Disclosure  — until `disclosureAckedAt` is set (compliance gate).
 *   2) Region setup — after disclosure, until `regionSetupAckedAt` is set.
 *   3) Onboarding  — after both acks (per-device, skippable).
 * No sensitive value is computed here; it only reads ack timestamps and renders
 * the existing modals.
 */
export function FirstRunGate() {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const prefsQuery = trpc.preferences.me.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const utils = trpc.useUtils();

  if (EXEMPT_ROUTES.some(r => location.startsWith(r))) return null;
  if (!isAuthenticated || !prefsQuery.data) return null;
  const prefs = prefsQuery.data as any;

  return (
    <>
      <DisclosureModal
        open={!prefs.disclosureAckedAt}
        onAck={() => utils.preferences.me.invalidate()}
      />
      <RegionSetupDialog
        open={!!prefs.disclosureAckedAt && !prefs.regionSetupAckedAt}
        onAck={() => utils.preferences.me.invalidate()}
      />
      <OnboardingTour
        prerequisitesMet={!!prefs.disclosureAckedAt && !!prefs.regionSetupAckedAt}
      />
    </>
  );
}
